import React, { FC, useContext, useEffect, useState } from 'react'
import { matchPath, Redirect, Route, Switch } from 'react-router'
import { BrowserRouter, useLocation } from 'react-router-dom'
import { useDispatch } from 'react-redux'
import { CssBaseline } from '@mui/material'
import { AppStateContext, useAuth } from './components/Context'
import { routes } from './constants/routes'
import { Login } from './pages/Login'
import { TwoFactorAuthentication } from './pages/Login/TwoFactorAuthentication'
import { CreateTwoFactorAuthentication } from './pages/Login/CreateTwoFactorAuthentication'
import { Home } from './pages/Home'
import { PrivacyPolicy } from './pages/PrivacyPolicy'
import { Contact } from './pages/Contact'
import { Header } from './components/Header'
import { Footer } from './components/Footer'
import { FormStepper } from './pages/FormStepper'
import { HeaderDashboard } from './components/Dashboard/Header'
import { FooterDashboard } from './components/Dashboard/Footer'
import { ThemeConfig, ThemeDashboardConfig } from './theme'
import { Account, AccountUser } from './pages/Dashboard/Account'
import { Trading } from './pages/Dashboard/Trading'
import { Treasury } from './pages/Dashboard/Trading/TreasuryManagment'
import { SpotTrading } from './pages/Dashboard/Trading/SpotTrading'
import { Staking } from './pages/Dashboard/Trading/Staking'
import { Portfolio } from './pages/Dashboard/Trading/Portfolio'
import { Details } from './pages/Dashboard/Trading/Portfolio/Summary/Details'
import { UserActiveSession } from './components/UserActiveSession'
import { ViewAllTransactions } from './pages/Dashboard/Trading/TreasuryManagment/TreasurySections/Transactions'
import { ViewAllTradesHistory } from './pages/Dashboard/Trading/TreasuryManagment/TreasurySections/TradeHistory'
import { ResetPassword } from './pages/User'
import { RequestResetPassword } from './pages/Login/RequestResetPassword'
import { getUserRequest } from './store/actions'
import { getCookie } from './utils'

export const RootRoutes: FC = () => {
  const { isUserLoggedIn } = useAuth()
  const location = useLocation()
  const { getAuth2FA } = useContext(AppStateContext)
  const [accessTwoFA, setAccessTwoFA] = useState<string | undefined>()
  const dispatch = useDispatch()

  useEffect(() => setAccessTwoFA(getAuth2FA), [setAccessTwoFA, getAuth2FA])

  const matchCompany = matchPath(location.pathname, {
    path: routes.COMPANY_ONBOARDING,
  })

  const matchUser = matchPath(location.pathname, {
    path: routes.USER_ONBOARDING,
  })

  useEffect(() => {
    const accessToken = getCookie('token')
    if (accessToken) {
      dispatch(getUserRequest(null))
    }
  }, [dispatch])

  return (
    <BrowserRouter>
      {/* eslint-disable-next-line no-nested-ternary */}
      {matchCompany || matchUser ? (
        <ThemeConfig>
          <Switch>
            <Route path={routes.COMPANY_ONBOARDING} exact component={FormStepper} />
            <Route path={routes.USER_ONBOARDING} exact component={FormStepper} />
          </Switch>
        </ThemeConfig>
      ) : isUserLoggedIn ? (
        <UserActiveSession>
          <ThemeDashboardConfig>
            <Switch>
              <Route>
                <HeaderDashboard />
                <CssBaseline />
                <Trading>
                  <Switch>
                    <Route path={routes.ACCOUNT_ADMIN} exact component={Account} />
                    <Route path={routes.ACCOUNT_USER} exact component={AccountUser} />
                    <Route path={routes.TREASURY} exact component={Treasury} />
                    <Route path={routes.VIEW_ALL_TRANSACTIONS_BY_ID} exact component={ViewAllTransactions} />
                    <Route path={routes.VIEW_ALL_TRANSACTIONS} exact component={ViewAllTransactions} />
                    <Route path={routes.VIEW_ALL_TRADES_BY_ID} exact component={ViewAllTradesHistory} />
                    <Route path={routes.VIEW_ALL_TRADES} exact component={ViewAllTradesHistory} />
                    <Route path={routes.SPOT_TRADING} exact component={SpotTrading} />
                    <Route path={routes.STAKING} exact component={Staking} />
                    <Route path={routes.PORTFOLIO} exact component={Portfolio} />
                    <Route path={routes.PORTFOLIO_FIAT} exact component={Details} />
                    <Route path={routes.PORTFOLIO_CRYPTO} exact component={Details} />
                    {/* <Redirect to={routes.SPOT_TRADING} /> */}
                    <Redirect to={routes.TREASURY} />
                  </Switch>
                </Trading>
                <FooterDashboard />
              </Route>
            </Switch>
          </ThemeDashboardConfig>
        </UserActiveSession>
      ) : (
        <ThemeConfig>
          <Switch>
            <Route>
              <Header />
              <CssBaseline />
              <Switch>
                <Route path={routes.LOGIN} exact component={Login} />
                <Route path={routes.USER_REQUEST_RESET_PASSWORD} exact component={RequestResetPassword} />
                <Route path={routes.USER_RESET_PASSWORD} exact component={ResetPassword} />
                {accessTwoFA && <Route path={routes.TWO_FA_LOGIN} exact component={TwoFactorAuthentication} />}
                {accessTwoFA && <Route path={routes.CREATE_TWO_FA} exact component={CreateTwoFactorAuthentication} />}
                <Route path={routes.HOME} exact component={Home} />
                <Route path={routes.PRIVACY_POLICY} exact component={PrivacyPolicy} />
                <Route path={routes.CONTACT} exact component={Contact} />
                <Redirect to={routes.LOGIN} />
              </Switch>
              <Footer />
            </Route>
          </Switch>
        </ThemeConfig>
      )}
    </BrowserRouter>
  )
}
